// Persistent dialler panel — bottom-dock on phones, side panel from sm+ upwards.
// Coordinates the Twilio service, the local store, and the disposition modal.

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { softphoneService } from './service';
import { IncomingRingtone, OutboundRingback, playEndCallTone } from './ringtone';
import { useSoftphone } from '../store/softphone';
import { useAuthStore } from '../store/auth';
import { ensureSocket } from '../lib/socket';
import { extractApiErrorMessage } from '../lib/api-error';
import { cn } from '../lib/utils';
import { DialPad } from './DialPad';
import { ActiveCall } from './ActiveCall';
import { DispositionModal } from './DispositionModal';
import { LeadContextPanel } from './LeadContext';
import { AgentStatusSelector } from './AgentStatusSelector';
import { CallbackOverdueDot } from '../components/CallbackBadge';
import { SoftphoneRecents } from '../calls/SoftphoneRecents';
import type { Call, Lead } from '@marnic/shared';

export function Softphone(): JSX.Element {
  const [showDisposition, setShowDisposition] = useState(false);
  const [endedCall, setEndedCall] = useState<Call | null>(null);
  const [endedLead, setEndedLead] = useState<Lead | null>(null);
  const [hangupError, setHangupError] = useState('');
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [showInCallDialer, setShowInCallDialer] = useState(false);
  const initRef = useRef(false);

  const status = useSoftphone((s) => s.status);
  const currentCall = useSoftphone((s) => s.currentCall);
  const currentLead = useSoftphone((s) => s.currentLead);
  const setStatus = useSoftphone((s) => s.setStatus);
  const setError = useSoftphone((s) => s.setError);
  const setMuted = useSoftphone((s) => s.setMuted);
  const startCall = useSoftphone((s) => s.startCall);
  const markAnswered = useSoftphone((s) => s.markAnswered);
  const clearRingingCall = useSoftphone((s) => s.clearRingingCall);
  const endCall = useSoftphone((s) => s.endCall);
  const campaignContext = useSoftphone((s) => s.campaignContext);
  const accessToken = useAuthStore((s) => s.accessToken);
  const queryClient = useQueryClient();
  const location = useLocation();

  useEffect(() => {
    if (!accessToken || initRef.current) return;
    initRef.current = true;
    setStatus('registering');

    void softphoneService
      .initialize()
      .then(() => {
        setStatus('ready');
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to initialise softphone';
        setError(msg);
      });

    const off = softphoneService.on((event) => {
      const store = useSoftphone.getState();
      switch (event.type) {
        case 'registered':
          setStatus('ready');
          break;
        case 'unregistered':
          setStatus('uninitialized');
          break;
        case 'sip_incoming':
          // call_started has already populated currentCall + currentLead and
          // set status to ringing_inbound (via store.startCall branching on
          // call.status === 'ringing'). Nothing further to do here — the
          // event is kept for symmetry with the Twilio incoming path.
          break;
        case 'incoming_canceled':
          // Caller hung up before the agent picked Accept/Reject, or another
          // tab won the forked INVITE. Drop the ringing UI without launching
          // the disposition modal (no real call took place on this device).
          clearRingingCall();
          break;
        case 'connecting':
          setStatus('connecting');
          break;
        case 'connected':
          // For inbound, this fires after the agent clicks Accept and the
          // SIP session reaches Established. Start the duration timer now.
          if (useSoftphone.getState().status === 'ringing_inbound' || useSoftphone.getState().status === 'connecting') {
            markAnswered();
          } else {
            setStatus('on_call');
          }
          break;
        case 'disconnected': {
          const liveCall = store.currentCall;
          const liveLead = store.currentLead;
          if (liveCall) {
            setEndedCall(liveCall);
            setEndedLead(liveLead);
            setShowDisposition(true);
          }
          endCall();
          break;
        }
        case 'mute_changed':
          setMuted(event.muted);
          break;
        case 'error':
          setError(event.message);
          break;
        default:
          break;
      }
    });

    const socket = ensureSocket(accessToken);
    const onCallStarted = ({ call, lead }: { call: Call; lead: Lead | null }): void => {
      console.log('[softphone] call_started recv', { callId: call.id, direction: call.direction, status: call.status });
      const ctx = useSoftphone.getState().campaignContext;
      startCall(call, lead, ctx);
    };
    const onCallEnded = ({ callId }: { callId: string }): void => {
      const store = useSoftphone.getState();
      if (store.currentCall?.id === callId) {
        // Server says this call is over. If our local provider session is
        // still bridged (signaling-vs-media race, AMD-triggered hangup, stale
        // status callback) the prospect's audio would keep playing under the
        // disposition modal — force the local session down so audio stops.
        if (softphoneService.isOnCall()) {
          softphoneService.forceEndActiveSession();
        }
        setEndedCall(store.currentCall);
        setEndedLead(store.currentLead);
        setShowDisposition(true);
        endCall();
      }
      // Refresh recents on every connected tab so the just-ended call appears.
      void queryClient.invalidateQueries({ queryKey: ['calls'] });
    };
    // For VP inbound calls the server sends incoming_call_preview (with
    // callId) before it originates the agent's SIP channel. Storing the callId
    // here means the SipBackend's onInvite handler can bind sipActiveCallId
    // correctly when the INVITE arrives moments later.
    const onIncomingPreview = ({ callId }: { callId?: string }): void => {
      if (callId) softphoneService.setIncomingCallId(callId);
    };
    socket.on('call_started', onCallStarted);
    socket.on('call_ended', onCallEnded);
    socket.on('incoming_call_preview', onIncomingPreview);

    return () => {
      off();
      socket.off('call_started', onCallStarted);
      socket.off('call_ended', onCallEnded);
      socket.off('incoming_call_preview', onIncomingPreview);
    };
  }, [accessToken, setStatus, setError, setMuted, startCall, markAnswered, clearRingingCall, endCall, queryClient]);

  const onDispositionSaved = (): void => {
    setShowDisposition(false);
    setEndedCall(null);
    setEndedLead(null);
    useSoftphone.getState().noteDisposition();
    // noteDisposition() already flips wrap_up → ready. Only force ready when
    // still in wrap-up — if the agent dialled a new call from the in-call
    // dialer, a fresh call may now be active and must not be clobbered.
    if (useSoftphone.getState().status === 'wrap_up') setStatus('ready');
  };

  const isInCall = status === 'on_call' || status === 'connecting';
  const isRingingInbound = status === 'ringing_inbound';
  // Outbound ringback only — inbound also briefly enters 'connecting' after
  // Accept while the SIP session establishes, but we don't want ringback in
  // that window because the agent has already heard the inbound chime and is
  // waiting on media to come up, not on the remote party.
  const isRingingOutbound = status === 'connecting' && currentCall?.direction === 'outbound';

  // Auto-expand the mobile dock when a call starts or starts ringing.
  useEffect(() => {
    if (isInCall || isRingingInbound) setMobileExpanded(true);
  }, [isInCall, isRingingInbound]);

  // Collapse the in-call dial pad again once the call has ended.
  useEffect(() => {
    if (!isInCall) setShowInCallDialer(false);
  }, [isInCall]);

  // Ringtone for inbound calls — starts when the SIP INVITE puts the dialer
  // into ringing_inbound, stops on Accept/Reject/cancel (any status change).
  useEffect(() => {
    if (!isRingingInbound) return;
    const ring = new IncomingRingtone();
    ring.start();
    return () => ring.stop();
  }, [isRingingInbound]);

  // Outbound ringback — UK PSTN-style "brrr-brrr" while we wait for the
  // dialled party to answer. Stops as soon as the call connects, ends, or
  // the agent cancels.
  useEffect(() => {
    if (!isRingingOutbound) return;
    const ring = new OutboundRingback();
    ring.start();
    return () => ring.stop();
  }, [isRingingOutbound]);

  // One-shot hangup beep when a call closes. wrap_up is only entered after a
  // real call has terminated (rejected/cancelled inbounds go straight back to
  // 'ready' via clearRingingCall, so they don't trigger this).
  const isWrapUp = status === 'wrap_up';
  useEffect(() => {
    if (isWrapUp) playEndCallTone();
  }, [isWrapUp]);

  useEffect(() => {
    if (location.pathname === '/call') setMobileExpanded(true);
  }, [location.pathname]);

  const statusTone =
    status === 'ready'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'on_call'
        ? 'bg-amber-100 text-amber-700'
        : status === 'ringing_inbound'
          ? 'bg-blue-100 text-blue-700 animate-pulse'
          : status === 'connecting'
            ? 'bg-blue-100 text-blue-700'
            : status === 'wrap_up'
              ? 'bg-purple-100 text-purple-700'
              : status === 'error'
                ? 'bg-red-100 text-red-700'
                : 'bg-ink-100 text-ink-600';

  return (
    <>
      {/* Phone-only collapsed dock — opens the sheet when tapped. */}
      <button
        type="button"
        onClick={() => setMobileExpanded(true)}
        className={cn(
          'sm:hidden fixed inset-x-0 bottom-0 z-30 h-14 px-4',
          'flex items-center gap-3 border-t border-ink-200 bg-white shadow-[0_-4px_12px_rgba(15,23,42,0.06)]',
          mobileExpanded && 'hidden',
        )}
      >
        <span className="text-lg" aria-hidden>📞</span>
        <span className={`badge ${statusTone}`}>{humaniseStatus(status)}</span>
        <CallbackOverdueDot />
        <span className="ml-auto text-xs text-ink-500">Open dialer ▲</span>
      </button>

      {/* Backdrop when the mobile sheet is expanded. */}
      {mobileExpanded && (
        <div
          className="sm:hidden fixed inset-0 z-30 bg-ink-900/30"
          onClick={() => {
            if (!isInCall) setMobileExpanded(false);
          }}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          // Mobile: bottom sheet
          'fixed inset-x-0 bottom-0 z-40 max-h-[88vh] rounded-t-2xl border-t border-ink-200 bg-white shadow-2xl',
          'flex flex-col',
          !mobileExpanded && 'hidden',
          // sm+: relative side panel
          'sm:relative sm:inset-auto sm:z-auto sm:max-h-none sm:rounded-none sm:shadow-none',
          'sm:flex sm:h-full sm:w-[320px] lg:w-[380px] 2xl:w-[420px] sm:border-t-0 sm:border-l sm:shrink-0',
        )}
      >
        {/* Mobile drag-handle */}
        <button
          type="button"
          aria-label="Collapse dialer"
          onClick={() => {
            if (!isInCall) setMobileExpanded(false);
          }}
          className="sm:hidden mx-auto mt-2 mb-1 h-1 w-12 shrink-0 rounded-full bg-ink-300"
        />

        <div className="flex flex-1 min-h-0 flex-col">
          <header className="shrink-0 border-b border-ink-200 px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-500">
                Softphone
                <CallbackOverdueDot />
              </h2>
              <span className={`badge ${statusTone}`}>{humaniseStatus(status)}</span>
            </div>
            <div className="mt-3">
              <AgentStatusSelector />
            </div>
          </header>

          <div className="scroll-area flex-1 min-h-0 p-3 sm:p-4 space-y-3 sm:space-y-4">
            {isRingingInbound ? (
              <IncomingCallPanel
                fromNumber={currentCall?.fromNumber ?? null}
                leadName={currentLead ? `${currentLead.firstName} ${currentLead.lastName}` : null}
                leadCompany={currentLead?.company ?? null}
              />
            ) : isInCall ? (
              <>
                <ActiveCall />
                <button
                  type="button"
                  className="btn-secondary w-full text-sm"
                  onClick={() => setShowInCallDialer((v) => !v)}
                >
                  {showInCallDialer ? '▲ Hide dialer' : '☎ Dial another number'}
                </button>
                {showInCallDialer && (
                  <div className="rounded-lg border border-ink-200 p-2">
                    <p className="mb-2 text-[11px] text-ink-500">
                      Placing a call here will end the current call first.
                    </p>
                    <DialPad />
                  </div>
                )}
              </>
            ) : (
              <DialPad />
            )}
            <LeadContextPanel />
            {!isInCall && !isRingingInbound && <SoftphoneRecents />}
          </div>

          {isRingingInbound && (
            <div className="shrink-0 border-t border-ink-200 p-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                className="btn-danger py-3 text-base"
                onClick={() => softphoneService.rejectIncoming()}
              >
                ✕ Reject
              </button>
              <button
                type="button"
                className="btn-primary py-3 text-base"
                onClick={() => softphoneService.acceptIncoming()}
              >
                ☎ Accept
              </button>
            </div>
          )}

          {isInCall && (
            <div className="shrink-0 border-t border-ink-200 p-3 space-y-1">
              <button
                type="button"
                className="btn-danger w-full py-3 text-base"
                onClick={() => {
                  setHangupError('');
                  // hangup() tears the local session down and emits
                  // 'disconnected' synchronously, so this only catches an
                  // unexpected throw — the /calls/:id/end POST is fire-and-
                  // forget and can no longer keep the agent on a dead call.
                  void softphoneService.hangup(currentCall?.id).catch((err: unknown) => {
                    setHangupError(extractApiErrorMessage(err, 'Failed to end call'));
                  });
                }}
              >
                ☎ End call
              </button>
              {hangupError && <div className="text-xs text-red-700 text-center">{hangupError}</div>}
            </div>
          )}
        </div>
      </aside>

      {showDisposition && endedCall && (
        <DispositionModal
          call={endedCall}
          lead={endedLead}
          campaignLeadId={campaignContext?.campaignLeadId}
          onClose={() => setShowDisposition(false)}
          onSaved={onDispositionSaved}
        />
      )}
    </>
  );
}

function humaniseStatus(s: string): string {
  switch (s) {
    case 'uninitialized':
      return 'Offline';
    case 'registering':
      return 'Connecting…';
    case 'ready':
      return 'Ready';
    case 'ringing_inbound':
      return 'Incoming call';
    case 'connecting':
      return 'Dialling';
    case 'on_call':
      return 'On call';
    case 'wrap_up':
      return 'Wrap-up';
    case 'error':
      return 'Error';
    default:
      return s;
  }
}

function IncomingCallPanel({
  fromNumber,
  leadName,
  leadCompany,
}: {
  fromNumber: string | null;
  leadName: string | null;
  leadCompany: string | null;
}): JSX.Element {
  return (
    <div className="card p-4 space-y-2 border-blue-200 bg-blue-50">
      <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">
        Incoming call
      </div>
      <div className="text-lg font-semibold text-ink-900">
        {leadName ?? fromNumber ?? 'Unknown caller'}
      </div>
      {leadCompany && <div className="text-sm text-ink-600">{leadCompany}</div>}
      {leadName && fromNumber && (
        <div className="text-xs text-ink-500 font-mono">{fromNumber}</div>
      )}
    </div>
  );
}
